import { z } from 'zod';
import { defineForm } from '../registry.js';
import { CONFIRMERS, dateText, evidenceFields, idString } from './common.js';

// 基本情報（フェースシート）。第1表①〜⑫・第4表①〜④・アセスメントへ供給する最上流。
// 旧: ClientProfile（名前・生年月日・要介護度）＋ ProfileDetailRecord（17 項目）＋ FamilyMemberRecord[]

// 旧 PROFILE_DETAIL_FIELDS（apps/api/src/types.ts）と同じ順・同じキー
export const PROFILE_DETAIL_FIELDS = [
  'sex',
  'address',
  'contact',
  'insuredNumber',
  'certifiedAt',
  'certValidFrom',
  'certValidTo',
  'limitAmount',
  'consultRoute',
  'receivedAt',
  'consultant',
  'socialResources',
  'certStatus',
  'planAuthor',
  'officeName',
  'planCreatedAt',
  'firstPlanCreatedAt',
] as const;
export type ProfileDetailField = (typeof PROFILE_DETAIL_FIELDS)[number];

// 旧 Web の ProfileView.tsx の選択肢（値は英字コード、表示は日本語）
export const CONSULT_ROUTE_OPTIONS = [
  { value: 'initial', label: '初回' },
  { value: 'referral', label: '紹介' },
  { value: 'continuation', label: '継続' },
] as const;
export const CERT_STATUS_OPTIONS = [
  { value: 'certified', label: '認定済' },
  { value: 'applying', label: '申請中' },
] as const;

const labelsOf = (opts: readonly { value: string; label: string }[]) => Object.fromEntries(opts.map((o) => [o.value, o.label]));

// 旧 API はこの 2 項目も自由文字列で受けていた（画面だけが選択式）。ここでは選択肢に絞る
const consultRoute = z
  .enum(['initial', 'referral', 'continuation'])
  .meta({ optionLabels: labelsOf(CONSULT_ROUTE_OPTIONS) })
  .describe('相談経路（初回・紹介・継続）');
const certStatus = z
  .enum(['certified', 'applying'])
  .meta({ optionLabels: labelsOf(CERT_STATUS_OPTIONS) })
  .describe('認定済・申請中');

export const profileSchema = z
  .object({
    name: z.string().min(1).describe('利用者名'),
    birthDate: dateText('生年月日').min(1).optional(),
    careLevel: z.string().min(1).optional().describe('要介護状態区分'),
  })
  .strict();

// 17 項目はすべて任意（旧: Partial<Record<ProfileDetailField, string>>）。長さ・日付の形は旧 API も未検査
export const profileDetailSchema = z
  .object({
    sex: z.string().optional().describe('性別'),
    address: z.string().optional().describe('住所'),
    contact: z.string().optional().describe('連絡先'),
    insuredNumber: z.string().optional().describe('被保険者番号'),
    certifiedAt: dateText('認定日').optional(),
    certValidFrom: dateText('認定有効期間（開始）').optional(),
    certValidTo: dateText('認定有効期間（終了）').optional(),
    limitAmount: z.string().optional().describe('区分支給限度額'),
    consultRoute: consultRoute.optional(),
    receivedAt: dateText('受付日').optional(),
    consultant: z.string().optional().describe('相談者'),
    socialResources: z.string().meta({ longtext: true }).optional().describe('利用中の制度・サービス'),
    certStatus: certStatus.optional(),
    planAuthor: z.string().optional().describe('居宅サービス計画作成者氏名'),
    officeName: z.string().optional().describe('居宅介護支援事業者・事業所名／所在地'),
    planCreatedAt: dateText('居宅サービス計画作成（変更）日').optional(),
    firstPlanCreatedAt: dateText('初回居宅サービス計画作成日').optional(),
  })
  .strict();

export const familyMemberSchema = z
  .object({
    memberId: idString.describe('家族ID'),
    name: z.string().min(1).describe('氏名'),
    relationship: z.string().optional().describe('続柄'),
    isKeyPerson: z.boolean().default(false).describe('キーパーソン'),
    emergencyContact: z.string().optional().describe('緊急連絡先'),
    note: z.string().optional().describe('備考'),
    ...evidenceFields,
  })
  .strict();

export const basicInfoSchema = z
  .object({
    profile: profileSchema.describe('本人（基本）'),
    profileDetail: profileDetailSchema.default({}).describe('本人・認定・受付・社会資源・計画作成'),
    families: z.array(familyMemberSchema).default([]).describe('家族'),
    ...evidenceFields,
  })
  .strict()
  .superRefine((v, ctx) => {
    const seen = new Set<string>();
    v.families.forEach((f, i) => {
      if (seen.has(f.memberId)) ctx.addIssue({ code: 'custom', path: ['families', i, 'memberId'], message: '家族IDが重複しています' });
      seen.add(f.memberId);
    });
  });
export type BasicInfoBody = z.infer<typeof basicInfoSchema>;

export const basicInfoForm = defineForm({
  app: 'caremanager',
  docType: 'basic_info',
  name: '基本情報',
  version: 1,
  schema: basicInfoSchema,
  confirmers: CONFIRMERS,
  description: '利用者の基本情報（フェースシート）。第1表①〜⑫と第4表①〜④の転記元',
});
